import { Box, MenuItem, Select, FormControl, InputLabel, Stack, Typography, CircularProgress, Button, Alert } from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { EditorProps } from '../../../definitions/report_builder/ReportBuilderDefinitions'
import { IMedia } from '../../../definitions/Media'
import ApiClient from '../../../backend/ApiClient'
import CustomTextField from '../../CustomTextField'
import useQuery, { buildQueryKey } from '../../hooks/useQuery'
import { changeRoute } from '../../../store/slices/SessionSlice'
import { PermMedia } from '@mui/icons-material'

const ImageEditor = ({ column, onChange }: EditorProps<'image'>) => {
    const [allMedia, setAllMedia] = useState<IMedia[]>([])
    const dispatch = useDispatch()

    const { response: allMediaRes, status: allMediaStatus } = useQuery({
        queryKey: buildQueryKey('allMedia'),
        queryFn: () => ApiClient.getAllMedia(),
        cache: false
    })

    useEffect(() => {
        if (allMediaStatus === 'success') {
            const imageMedia = allMediaRes.filter((x: IMedia) =>
                x.type === 'image' &&
                (x.name.toLowerCase().endsWith('.png') || x.name.toLowerCase().endsWith('.jpeg'))
            )
            setAllMedia(imageMedia)
        }
    }, [allMediaRes, allMediaStatus])

    const selectedFileKey = column.settings.fileKey ?? ''
    const validSelectedFileKey = allMedia.some(media => media.filekey === selectedFileKey) ? selectedFileKey : ''
    const isLoading = allMediaStatus === 'loading'

    const handleGoToMediaManager = () => {
        dispatch(changeRoute({
            route: 'media'
        }))
    }

    return (
        <Box display='flex' flexDirection='column' gap={2}>
            <FormControl fullWidth size='small'>
                <InputLabel>Select Image</InputLabel>
                <Select
                    value={validSelectedFileKey}
                    onChange={e => {
                        const newFileKey = e.target.value
                        onChange({
                            ...column.settings,
                            fileKey: newFileKey
                        })
                    }}
                    label='Select Image'
                    renderValue={value => {
                        if (isLoading) {
                            return (
                                <Stack direction='row' alignItems='center' gap={1}>
                                    <CircularProgress size={16} />
                                    <Typography variant='body2'>Loading images...</Typography>
                                </Stack>
                            )
                        }

                        const media = allMedia.find(m => m.filekey === value)
                        return media ? media.name : 'No image selected'
                    }}>
                    {isLoading
                        ? (
                                <MenuItem disabled>
                                    <Stack direction='row' alignItems='center' gap={1}>
                                        <CircularProgress size={16} />
                                        <Typography color='text.secondary'>Loading images...</Typography>
                                    </Stack>
                                </MenuItem>
                            )
                        : [
                                <MenuItem key='empty' value=''>
                                    <Typography color='text.secondary'>No image selected</Typography>
                                </MenuItem>,
                                ...(allMedia.length === 0
                                    ? [
                                            <MenuItem key='no-images' disabled>
                                                <Typography color='text.secondary'>No images found</Typography>
                                            </MenuItem>
                                        ]
                                    : allMedia.map(media => (
                                        <MenuItem key={media.filekey} value={media.filekey}>
                                            <Stack>
                                                <Typography variant='body2' fontWeight={500}>
                                                    {media.name}
                                                </Typography>
                                                <Typography variant='caption' color='text.secondary'>
                                                    {media.type}
                                                </Typography>
                                            </Stack>
                                        </MenuItem>
                                    ))
                                )
                            ]}
                </Select>
            </FormControl>

            <Alert
                severity='info'
                action={(
                    <Button
                        startIcon={<PermMedia />}
                        color='secondary'
                        size='small'
                        onClick={handleGoToMediaManager}
                        variant='outlined'>
                        Media
                    </Button>
                )}>
                To view or upload additional images for reports, visit the Media Manager
            </Alert>

            <CustomTextField
                label='Image Height'
                type='number'
                value={column.settings.height || ''}
                onValueChange={height => onChange({ ...column.settings, height: parseInt(height) || 0 })}
            />

            <FormControl fullWidth size='small'>
                <InputLabel>Alignment</InputLabel>
                <Select
                    value={column.settings.alignment || 'left'}
                    onChange={e => onChange({ ...column.settings, alignment: e.target.value })}
                    label='Alignment'>
                    <MenuItem value='left'>Left</MenuItem>
                    <MenuItem value='center'>Center</MenuItem>
                    <MenuItem value='right'>Right</MenuItem>
                </Select>
            </FormControl>
        </Box>
    )
}

export default ImageEditor
